import { useMemo, useState } from "react";
import { Dropdown } from "../Dropdown";
import {
  HAZARD_LABEL,
  HAZARD_OPTIONS,
  SCOPE_LABEL,
  eventHazards,
  eventTitle,
  formatIsoDate,
} from "../../lib/explorer";
import type { BlockingEvent, DataScope, EventHazard } from "../../types/explorer";

interface EventTilesProps {
  events: BlockingEvent[];
  selectedDate: string;
  onSelectEvent: (event: BlockingEvent) => void;
  scopeFilter: "all" | DataScope;
  onScopeFilterChange: (scope: "all" | DataScope) => void;
}

export function EventTiles({
  events,
  selectedDate,
  onSelectEvent,
  scopeFilter,
  onScopeFilterChange,
}: EventTilesProps) {
  const [hazardFilter, setHazardFilter] = useState<"all" | EventHazard>("all");
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return events.filter((event) => {
      if (
        hazardFilter !== "all" &&
        !eventHazards(event).includes(hazardFilter)
      ) {
        return false;
      }
      if (!needle) return true;
      const hay = [
        eventTitle(event),
        event.region,
        event.notes ?? "",
        event.startDate,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [events, hazardFilter, query]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex flex-wrap items-center gap-2">
          <Dropdown
            label="Scope"
            size="sm"
            value={scopeFilter}
            onChange={onScopeFilterChange}
            options={[
              { value: "all", label: "All events" },
              {
                value: "validated-historical",
                label: SCOPE_LABEL["validated-historical"],
              },
              { value: "extended-2026", label: SCOPE_LABEL["extended-2026"] },
            ]}
          />
          <Dropdown
            label="Event type"
            size="sm"
            value={hazardFilter}
            onChange={setHazardFilter}
            options={[
              { value: "all", label: "All types" },
              ...HAZARD_OPTIONS.map((hazard) => ({
                value: hazard,
                label: HAZARD_LABEL[hazard],
              })),
            ]}
          />
        </div>

        <label className="sr-only" htmlFor="event-search">
          Search events
        </label>
        <input
          id="event-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search region, year…"
          className="w-full min-w-0 rounded-lg border border-line/35 bg-surface px-2.5 py-2 text-xs text-ink outline-none focus:border-accent sm:min-w-[10rem] sm:flex-1 sm:py-1.5"
        />
      </div>

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
        {visible.length === 0 ? (
          <p className="px-1 py-6 text-sm text-muted">
            No events match these filters.
          </p>
        ) : (
          visible.map((event) => {
            const active =
              selectedDate >= event.startDate && selectedDate <= event.endDate;
            return (
              <button
                key={event.id}
                type="button"
                onClick={() => onSelectEvent(event)}
                className={[
                  "w-full rounded-lg border px-3 py-2.5 text-left transition sm:py-2",
                  active
                    ? "border-accent bg-accent/15 text-ink"
                    : "border-line/30 bg-surface text-muted hover:border-accent/50 hover:text-ink",
                ].join(" ")}
              >
                <span className="flex items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-ink">
                    {eventTitle(event)}
                  </span>
                  <span className="shrink-0 text-[11px] text-faint">
                    {eventHazards(event).map((hazard) => HAZARD_LABEL[hazard]).join(" · ")}
                  </span>
                </span>
                <span className="mt-0.5 block text-[11px] text-faint">
                  {event.region} · {formatIsoDate(event.startDate)}
                  {event.endDate !== event.startDate
                    ? ` – ${formatIsoDate(event.endDate)}`
                    : ""}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
